import React from 'react';

import { useNavigate } from 'react-router-dom';

import CustomModal from 'components/CustomModal/CustomModal';

import { Button } from 'components/Button';

import API from 'utils/api';

import { useStore } from 'utils/hooks/store';

import routes from 'core/routes';

interface DocumentDeleteBlockedModalProps {
  documentUuid: string;
  onClose: () => void;
}

const DocumentDeleteBlockedModal: React.FunctionComponent<DocumentDeleteBlockedModalProps> = ({
  documentUuid,
  onClose,
}) => {
  const navigate = useNavigate();
  const { session, notification, folders } = useStore();
  const [activeCount, setActiveCount] = React.useState<number | null>(null);
  const [documentName, setDocumentName] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      API.entities.listAcknowledgementRequests({ document: documentUuid, status: 'active', limit: 1 })(),
      API.entities.listAcknowledgementRequests({ document: documentUuid, limit: 1 })(),
    ])
      .then(([active, any]) => {
        if (cancelled) return;
        setActiveCount(active.data.count);
        const name = active.data.results?.[0]?.document?.name ?? any.data.results?.[0]?.document?.name;
        if (name) setDocumentName(name);
      })
      .catch(() => {
        // Modal still works without the count — leave the fallback copy in place.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentUuid]);

  const blockedByCompleted = activeCount === 0;

  const handleView = (): void => {
    onClose();
    if (session.user?.documentAcknowledgementsEnabled) {
      const documentNameParam = documentName ? `document_name=${encodeURIComponent(documentName)}&` : '';
      const status = blockedByCompleted ? 'completed' : 'active';
      folders.clearSelectedDocument();
      navigate(`${routes.dashboard.documents.acknowledgementRequests.toString()}?${documentNameParam}status=${status}`);
    } else {
      notification.enqueueInfoSnackbar('Acknowledgement requests are no longer enabled for your organisation.');
      navigate(routes.dashboard.documents.toString());
    }
  };

  const body = blockedByCompleted
    ? 'This document has been sent for acknowledgement and must be retained for audit purposes.'
    : activeCount == null
      ? 'This document has active acknowledgement requests. Cancel them first, then try again.'
      : `This document has ${activeCount} active acknowledgement request${activeCount === 1 ? '' : 's'}. Cancel them first, then try again.`;

  return (
    <CustomModal
      openModal
      onClose={onClose}
      modalTitle="Cannot delete this document"
      modalDescription={body}
      cancelButton={
        <Button variant="outlined" size="large" onClick={onClose}>
          Close
        </Button>
      }
      submitButton={
        <Button variant="contained" size="large" onClick={handleView} disabled={loading}>
          View requests
        </Button>
      }
    />
  );
};

export default DocumentDeleteBlockedModal;
